import {Component, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule} from '@angular/forms';
import {I18nService} from '../../../_services/gui/i18n.service';
import {User, UserIpRestriction, UserTimeRestriction} from '../../../_data/admin/user';
import {BehaviorSubject, of, Subscription, zip} from 'rxjs';
import {AddUser} from '../../../_data/admin/add-user';
import {UsersService} from '../../../_services/data/users.service';
import {NotificationService} from '../../../_services/gui/notification.service';
import {ActivatedRoute, Router} from '@angular/router';
import {CompaniesService} from '../../../_services/data/companies.service';
import {Company} from '../../../_data/admin/company';
import {BranchesService} from '../../../_services/data/branches.service';
import {Branch} from '../../../_data/admin/branch';
import {HttpResponse} from '@angular/common/http';
import {first, map, mergeMap, tap} from 'rxjs/operators';
import {IP_PATTERN, PERSON_NAME_PATTERN} from 'src/app/_constants/constants';
import {exists} from '../../../_utils/operators';
import {enterOneOf, fieldsMatch, phoneValidator} from '../../../_utils/validators';
import {AuthenticationService} from '../../../_services/authentication.service';
import {setValidators} from '../../../_utils/forms';
import {dayNumOfWeek, nullIfBlank} from '../../../_utils/lang';
import {AccessTimesComponent} from './access-times/access-times.component';
import {NgClass, AsyncPipe, KeyValuePipe} from '@angular/common';
import {ValidationErrorsComponent} from '../../../common/forms/validation-errors/validation-errors.component';
import {PhoneFormRowComponent} from '../../../common/forms/phone-form-row/phone-form-row.component';
import {RequiredComponent} from '../../../common/forms/required/required.component';
import {NgxMaskModule} from 'ngx-mask';
import {TranslateModule} from '@ngx-translate/core';

/**
 * Component for the create/edit user form.
 */
@Component({
    selector: 'app-create-user',
    templateUrl: './create-user.component.html',
    styleUrls: ['./create-user.component.scss'],
    imports: [ReactiveFormsModule, NgClass, ValidationErrorsComponent, PhoneFormRowComponent, 
      AccessTimesComponent, RequiredComponent, NgxMaskModule, AsyncPipe, KeyValuePipe, TranslateModule]
})
export class CreateUserComponent implements OnInit, OnDestroy {

  //TODO: make a helper service for various places to pass already loaded data to this component.
  //  A company, thin user, or full user could be passed and loading/reloading from the router state/url got complicated so was removed.

  @ViewChild(AccessTimesComponent) accessTimesComponent: AccessTimesComponent;

  isForSpecificCompany = false; //As opposed to being a TU Admin adding a user and needing to choose the company as part of the process.
  manualCompanyFound = true;
  companyUserType: BehaviorSubject<'admin' | 'user'> = new BehaviorSubject<'admin' | 'user'>('user');
  branchNames: string[] = [];
  createWhat: string;

  // We have this subject so that child components can easily monitor the overall form submitted state.
  formSubmitted$ = new BehaviorSubject<boolean>(false);
  formSubmitted: boolean;

  createUserFormGroup: UntypedFormGroup;

  companyNameParam: string;
  userIdParam: string;
  companyFailedToLoad: boolean = false;
  userIdFailedToLoad: boolean = false;

  readonly i18nService = inject(I18nService);

  private readonly companySubject: BehaviorSubject<Company> = new BehaviorSubject<Company>(null);
  private companySubscription: Subscription;
  private createWhatSubscription: Subscription;
  private companyName: string;
  private isEditing = false;
  private existingUserName: string;
  private userBeingEdited: User;
  private isTuAdmin: boolean;
  private companyBypassIp: boolean;

  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly usersService = inject(UsersService);
  private readonly notificationService = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly companiesService = inject(CompaniesService);
  private readonly branchesService = inject(BranchesService);
  private readonly authService = inject(AuthenticationService);

  constructor() {

    //Set a default title (will be changed later in loadEditingUser() if editing)
    this.setCreateWhat('content.add-user');

    this.route.data.subscribe(value => {
      // Check the route data to see if we're editing a user
      if(value?.editing) { //Check it
        //We're editing!
        console.info(`Edit user`);
        this.isEditing = true;
        this.i18nService.setTitle('title.admin.users.edit-user');
        //Load the editing user and the company that belongs to the user
        this.loadEditingUser();
      } else {
        //Not editing
        console.info('Create user');
        this.i18nService.setTitle('title.admin.users.create');

        this.companyNameParam = this.route.snapshot.params.companyName;
        if(!!this.companyNameParam) {
          of(value).pipe(
            mergeMap((data) =>
              //Load company details from the company url param
              zip(of(data), this.companiesService.getCompanyDetails(this.companyNameParam).pipe(first(), tap(c => this.companyFailedToLoad = !c), exists()))
            )
          ).subscribe(([data, c]) => {

            console.debug(`Incoming data.  Data: ${JSON.stringify(data)}, company name: ${c?.companyName}`);
            this.companySubject.next(c);

            console.debug(`setting companyUserType to ${data.userType} from route data`);
            this.companyUserType.next(data.userType);

            //Set the subtitle accordingly
            this.i18nService.getTranslation(`content.${data.userType}`).subscribe(userType => {
              this.setCreateWhat('content.add-company-user', {userType: userType, companyName: c.companyName});
            });
          });
        }  
      }
    });

    //Tie the formSubmitted field to the Subject state
    this.formSubmitted$.subscribe(v => this.formSubmitted = v);

  }

  private static timeRestrictionsFromForm(value: any): UserTimeRestriction[] {
    return Object.entries(value.allowedTimes).map((x: [string, any]) => {
      console.debug(`Time Restriction in:`, x);
      const v = ({
        dayOfWeek: String(dayNumOfWeek(x[0])),
        allDay:    x[1].allowed === 'all-day' ? 1 : (x[1].allowed === 'n' ? -1 : 0),
        timeFrom:  x[1].startTime,
        timeTo:    x[1].endTime
      }) as UserTimeRestriction;
      console.debug(`Time Restriction out:`, v);
      return v;
    });
  }

  private static ipRestrictionsFromForm(value: any): UserIpRestriction[] {
    return Object.entries(value.ipRestrictions)
      .filter((x: [string, any]) => !!x[1].startIp || !!x[1].endIp)
      .map((x: [string, any]) => ({ipFrom: x[1].startIp, ipTo: x[1].endIp, ipNumber: 0}));
  }

  /**
   * An IP is optional if the company has IP Bypass checked
   */
  private ipRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => this.companyBypassIp ? null : Validators.required(control);
  }

  /** If the user being created is of type "user", it needs at least one branch assigned */
  private branchesValidator(): ValidatorFn {
    const baseValidator = enterOneOf(this.branchNames);
    return control => {
      if(this.companyUserType.value === 'user') {
        return baseValidator(control);
      } else {
        return null;
      }
    };
  }

  /** When creating a user for a company, we have to load the branches for that company and update the FormGroup with appropriate controls */
  private loadBranches(companyName: string) {
    zip(this.branchesService.getBranches(companyName), this.companyUserType).subscribe((o: [HttpResponse<Branch[]>, 'admin' | 'user']) => {
      const response = o[0];
      const companyUserType = o[1];
      const memberCodeControls: {[p: string]: any} = {};

      console.debug(`Populating branches.  Checking any as appropriate.  User type: ${companyUserType}.`);
      const branchesToCheck = this.userBeingEdited ? this.userBeingEdited.branchDetails.map(branchDetails => branchDetails.branchName) : [];
      response.body?.forEach(branch => memberCodeControls[branch.branchName] = branchesToCheck.find(b => b === branch.branchName) && 'true' || '');
      this.branchNames = response.body?.map(branch => branch.branchName) ?? [];

      //If adding a normal user, a branch must be chosen.  If an admin, it's optional.
      // const options = companyUserType === 'user' ? {validators: enterOneOf(this.branchNames)} : {};
      this.createUserFormGroup.setControl('memberCodes', this.formBuilder.group(memberCodeControls, {validators: this.branchesValidator()}));
    });
  }

  /** Loads a user's details and updates the form for editing */
  private loadEditingUser() {

    this.userIdParam = this.route.snapshot.params.userId;

    //Load the user we need to edit from the url param
    this.usersService.getUserDetails(this.userIdParam).pipe(
      map((response: HttpResponse<User>) => response.body),
      tap(user => this.userIdFailedToLoad = !user),
      exists(),
      first()
    ).subscribe((u: User) => {

      // Update the primary title
      this.setCreateWhat('content.edit-user', {username: u.userName});

      this.userBeingEdited = u;

      //Load the simple form values
      console.debug(`User loaded: ${JSON.stringify(u)}`);
      this.createUserFormGroup.controls.firstName.setValue(u.firstName);
      this.createUserFormGroup.controls.lastName.setValue(u.lastName);
      this.createUserFormGroup.controls.email.setValue(u.email);
      this.createUserFormGroup.controls.confirmEmail.setValue(u.email);
      this.createUserFormGroup.controls.middleName.setValue(u.middleName);
      this.createUserFormGroup.controls.phone.setValue(u.phone);
      this.createUserFormGroup.controls.extension.setValue(u.extension);
      this.createUserFormGroup.controls.languagePreference.setValue(u.language);
      this.createUserFormGroup.controls.company.setValue(u.company);

      //Load the time restrictions
      this.accessTimesComponent.applyTimes(u.timeZone, u.timeRestrictions);

      //Load the ip restrictions
      u.ipRestrictions.forEach((ipRestriction, index) => {
        if(index > 0) {
          this.insertIp();
        }
        this.setIp(ipRestriction.ipFrom, ipRestriction.ipTo);
      });

      this.existingUserName = u.userName;
      if(!!u.company) {
        console.debug(`Loading company details from user company ${u.company}`);
        this.companiesService.getCompanyDetails(u.company).subscribe(c => {
          this.companySubject.next(c);
        });
      }
    });
  }

  /**
   * Gets the max key from the IP restrictions FormGroup.
   * Used when adding a new IP restriction or removing one since those operations need to know what the last restriction currently is
   */
  private maxIpRestrictionKey(): number {
    return Math.max(...Object.keys(this.ipRestrictionsFormGroup.controls).map(v => Number(v)));
  }

  /** Sets the ip range to the currently last control in the ipRestrictions form group */
  private setIp(startIp: string, endIp: string) {
    const formGroup: UntypedFormGroup = this.ipRestrictionsFormGroup.controls[this.maxIpRestrictionKey()] as UntypedFormGroup;
    formGroup.controls.startIp.setValue(startIp);
    formGroup.controls.endIp.setValue(endIp);
  }

  /** Creates a new config object for setting up an ip restriction range in a form group */
  private newIpRestrictionControlsConfig() {
    const ipPatternValidator = Validators.pattern(RegExp(IP_PATTERN));
    const ipValidator = Validators.compose([this.ipRequiredValidator(), ipPatternValidator]);
    return {startIp: ['', ipValidator], endIp: ['', ipValidator]};
  }

  /** Based on what the company user type is, change the validators for the phone field (required/optional) */
  private updatePhoneValidatorsOnCompanyUserType() {
    this.companyUserType.subscribe(userType => {
      console.debug(`companyUserType changed to ${userType}`);
      if(userType === 'admin') {
        setValidators(this.createUserFormGroup.controls.phone, [Validators.required, phoneValidator]);
      } else {
        setValidators(this.createUserFormGroup.controls.phone, phoneValidator);
      }
      this.createUserFormGroup.controls.memberCodes.updateValueAndValidity();
    });
  }

  private createOrEditUser$(addUser: AddUser, value: any) {
    if(this.isEditing) {
      addUser.userName = this.existingUserName;
      console.debug('Updating user...', addUser);
      return this.usersService.editUser(addUser);
    } else {
      console.debug('Adding user...');
      //Figure out a valid username - USERNAME123
      return this.usersService.validUserNameVariant(`${value.firstName}${value.lastName}`)
        .pipe(
          //Once found, switch the observable over to actually adding the user
          mergeMap(userNameVariant => {
            addUser.userName = userNameVariant;
            //But first, quickly get the user's type.
            return this.companyUserType.pipe(mergeMap(companyUserType => this.usersService.addUser(addUser, companyUserType)));
          })
        );
    }
  }

  /** Updates part of the page heading.  "Create User", "Create Admin", "Create User for ACME Inc." */
  protected setCreateWhat(i18nKey: string, data: Record<string, unknown> = {}) {
    if(this.createWhatSubscription) {
      this.createWhatSubscription.unsubscribe();
    }
    this.createWhatSubscription = this.i18nService.getTranslation(i18nKey, data).subscribe(value => this.createWhat = value);
  }

  /** init lifecycle */
  ngOnInit() {

    this.authService.isTuAdmin$.pipe(first()).subscribe(isTuAdmin => {
      this.isTuAdmin = isTuAdmin;

      this.createUserFormGroup = this.formBuilder.group({
        firstName:          ['', Validators.compose([Validators.required, Validators.pattern(PERSON_NAME_PATTERN)])],
        lastName:           ['', Validators.compose([Validators.required, Validators.pattern(PERSON_NAME_PATTERN)])],
        email:              ['', Validators.compose([Validators.required, Validators.email])],
        confirmEmail:       ['', Validators.compose([Validators.required, Validators.email, fieldsMatch('email')])],
        middleName:         ['', Validators.pattern(PERSON_NAME_PATTERN)],
        phone:              '', //Don't set phone validators here.  See updatePhoneValidatorsOnCompanyUserType() which conditionally sets the validators
        extension:          '',
        languagePreference: this.i18nService.currentLangShortCode,
        company:            ['', Validators.required],
        companyUserType:    ['user', Validators.required],
        memberCodes:        this.formBuilder.group({}),
        timeZone:           '', //Will be updated by the AccessTimes component
        allowedTimes:       this.formBuilder.group({}), //Will be updated by the AccessTimes component
        ipRestrictions:     this.formBuilder.group({
          1: this.formBuilder.group(this.newIpRestrictionControlsConfig())
        })
      });
    });

    //If the company is loaded while navigating to the form, remove the manual company name / user type fields and load the branches.
    this.companySubscription = this.companySubject.pipe(exists()).subscribe((c: Company) => {
      console.debug(`Loaded company ${JSON.stringify(c)}`);
      this.isForSpecificCompany = true;
      this.companyName = c.companyName;
      this.companyBypassIp = c.bypassIPCheck === 1;
      this.createUserFormGroup.removeControl('company');
      this.createUserFormGroup.removeControl('companyUserType');
      this.loadBranches(c.companyName);

      //The ipRestrictions validators can be dependent on the companyBypassIPCheck, so now that we know that, we'll create the first
    });

    this.createUserFormGroup.controls.companyUserType.valueChanges.pipe(exists()).subscribe(v => {
      console.debug(`setting companyUserType to ${v} from valueChange`);
      this.companyUserType.next(v);
    });

    this.updatePhoneValidatorsOnCompanyUserType();

  }

  /** destroy lifecycle */
  ngOnDestroy(): void {
    this.companySubscription.unsubscribe();
  }

  /**
   * When adding a user from the main users page, instead of from a company's page, the create-user form will have a text box asking for the company name.
   * When a company name is entered in that text box and submitted, the branches will be loaded for it by this method.
   */
  manuallyTypedCompanyName() {
    //If the need comes up to allow a user to instinctively press Enter in the company field without submitting the form, see here: https://stackoverflow.com/a/48412130/475735
    const companyNameControl = this.createUserFormGroup.controls.company;
    const companyName = companyNameControl.value?.toUpperCase();
    console.debug(`Loading branches for company ${companyName}...`);
    this.companiesService.getCompanyDetails(companyName).subscribe((c: Company) => {
      this.manualCompanyFound = !!c;
      if(c) {
        this.loadBranches(companyName);
        this.companyBypassIp = c.bypassIPCheck === 1; //This will update whether or not the IP restrictions are required based on the new company
      } else {
        console.info(`Company ${companyName} not found`);
        this.branchNames = [];
      }
    });
  }

  /**
   * Adds a new control to the form group for the added IP range the user wants
   */
  insertIp() {
    const maxKey = this.maxIpRestrictionKey();
    this.ipRestrictionsFormGroup.addControl(String(maxKey + 1), this.formBuilder.group(this.newIpRestrictionControlsConfig()));
  }

  /**
   * Removes the control from the form group for the specified IP range
   */
  removeIp(key: string) {
    console.debug(`Removing ip restriction ${key}`);
    this.ipRestrictionsFormGroup.removeControl(key);
  }

  /** Returns how many current IP restrictions there are */
  ipRestrictionsCount(): number {
    return Object.entries(this.ipRestrictionsFormGroup.controls).length;
  }

  /** Submit the create-user form.  Ensuring it's valid and transforming any data as needed */
  submit(value: any): void {
    console.debug(JSON.stringify(value));

    this.formSubmitted$.next(true);
    if(this.createUserFormGroup.valid) {

      const checkedBranches: string[] = Object.entries(value.memberCodes).filter(([, v]) => !!v).map(([k]) => k) || [];
      console.debug('Checked branches', checkedBranches);

      const timeRestrictions = CreateUserComponent.timeRestrictionsFromForm(value);
      const ipRestrictions = CreateUserComponent.ipRestrictionsFromForm(value);
      console.debug(ipRestrictions);

      const addUser: AddUser = {
        company:          this.isForSpecificCompany ? this.companyName : value.company?.toUpperCase(),
        email:            value.email,
        firstName:        value.firstName,
        language:         value.languagePreference,
        lastName:         value.lastName,
        middleName:       value.middleName,
        phone:            nullIfBlank(value.phone),
        userName:         null,
        branches:         checkedBranches,
        extension:        value.extension,
        ipRestrictions:   ipRestrictions,
        timeZone:         Number(value.timeZone),
        timeRestrictions: timeRestrictions,
        creationDt:       '',
        department:       '',
        lastLoginDt:      '',
        passwordExpiryDt: '',
        roles:            '',
        status:           '',
        title:            ''
      };

      this.createOrEditUser$(addUser, value).subscribe((success: boolean) => {
        if(success) {
          this.router.navigateByUrl(this.router.createUrlTree(['/admin/users', addUser.userName, 'details'])).then(() => console.debug('Nav to user details complete'));
        } else {
          this.notificationService.displayI18n('content.error-occurred');
        }
      });
    }
  }

  /** Casts the IP restriction FormGroup as such for the provided index */
  ipRestrictionFormGroup(index): UntypedFormGroup {
    return this.ipRestrictionsFormGroup.controls[index] as UntypedFormGroup;
  }

  /** Handles when the cancel button is clicked and the user needs to be sent back from whence they came */
  cancel() {
    // We can know where to intelligently send the user back based on our current state:
    //  - isForSpecificCompany is only true when the user clicks "add" from either the users or admins tab of a company.
    //    It's false when the user clicks add from the company-wide users overview
    //  - companyUserType observable stores admins/users based on the origin (if a user was on the admins tab or users tab)
    if(this.isForSpecificCompany) {
      const returnTab = this.companyUserType.value === 'admin' ? 'admins' : 'users';
      console.debug(`Should send back to ${returnTab} tab`);
      this.router.navigateByUrl(
        this.router.createUrlTree(['/admin/companies', this.companyName, 'details', returnTab])
      ).then(() => console.debug('Nav to company details complete'));
    } else {
      this.router.navigateByUrl(`admin/users`).then(() => console.debug('Nav to users complete'));
    }
  }

  public get ipRestrictionsLength(): number {
    return Object.keys(this.ipRestrictionsFormGroup.controls).length;
  }

  public get f(): UntypedFormGroup {
    return this.createUserFormGroup;
  }

  public get c(): {[p: string]: AbstractControl} {
    return this.createUserFormGroup.controls;
  }

  public get memberCodesFormGroup(): UntypedFormGroup {
    return this.createUserFormGroup.controls.memberCodes as UntypedFormGroup;
  }

  public get ipRestrictionsFormGroup(): UntypedFormGroup {
    return this.createUserFormGroup.controls.ipRestrictions as UntypedFormGroup;
  }

  public get ipRestrictionsControlsMap(): Map<string, AbstractControl> {
    return new Map(Object.entries(this.ipRestrictionsFormGroup.controls));
  }
}
